<script setup>
import { reactive, computed, ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import ReminderTemplatesEditor from 'dashboard/components-next/appointments/ReminderTemplatesEditor.vue';

const props = defineProps({
  service: {
    type: Object,
    default: null,
  },
  isSubmitting: {
    type: Boolean,
    default: false,
  },
  // AP-03: backend validation message surfaced by the parent (e.g. duplicate name)
  serverError: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['close', 'submit']);

const { t } = useI18n();

const isEditing = computed(() => !!props.service?.id);

// L-02: price displayed in BRL (R$), stored in cents
const priceBRL = ref(
  ((props.service?.default_price_cents ?? 0) / 100).toFixed(2)
);

const form = reactive({
  name: props.service?.name || '',
  description: props.service?.description || '',
  duration_minutes: props.service?.duration_minutes || 30,
  default_price_cents: props.service?.default_price_cents || 0,
  color: props.service?.color || '#10B981',
  online_available: props.service?.online_available ?? true,
  reminder_templates: props.service?.reminder_templates || [],
});

function onPriceInput(event) {
  const val = parseFloat(event.target.value) || 0;
  priceBRL.value = event.target.value;
  form.default_price_cents = Math.round(val * 100);
}

// AP-03: inline client-side validation. Previously this returned silently and the
// Save button was just disabled, so the user got no feedback about WHY they
// couldn't save. Now the button stays enabled and a click surfaces the reason.
const clientErrors = reactive({ name: '', duration_minutes: '' });

const handleSubmit = () => {
  clientErrors.name = form.name?.trim()
    ? ''
    : t('APPOINTMENTS.VALIDATION.NAME_REQUIRED');
  clientErrors.duration_minutes =
    form.duration_minutes > 0
      ? ''
      : t('APPOINTMENTS.VALIDATION.DURATION_REQUIRED');
  if (clientErrors.name || clientErrors.duration_minutes) return;
  emit('submit', { ...form });
};

// H6: Focus management
const dialogRef = ref(null);
let triggerEl;
onMounted(() => {
  triggerEl = document.activeElement;
  const focusable = dialogRef.value?.querySelector(
    'input,select,button,textarea,[tabindex]:not([tabindex="-1"])'
  );
  focusable?.focus();
});
onUnmounted(() => triggerEl?.focus?.());
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    role="dialog"
    aria-modal="true"
    aria-labelledby="service-form-title"
  >
    <div
      ref="dialogRef"
      class="bg-n-background rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
    >
      <div
        class="flex items-center justify-between px-6 py-4 border-b border-n-strong"
      >
        <h2
          id="service-form-title"
          class="text-lg font-semibold text-n-slate-12"
        >
          {{
            isEditing
              ? t('APPOINTMENTS.SERVICES.EDIT')
              : t('APPOINTMENTS.SERVICES.ADD')
          }}
        </h2>
        <button
          type="button"
          class="text-n-slate-10 hover:text-n-slate-11"
          :aria-label="t('APPOINTMENTS.CLOSE')"
          @click="$emit('close')"
        >
          <span class="i-lucide-x size-5" aria-hidden="true" />
        </button>
      </div>

      <div class="overflow-y-auto flex-1 px-6 py-4 space-y-4">
        <!-- AP-03: backend validation message (e.g. duplicate name) -->
        <div
          v-if="serverError"
          class="rounded-lg border border-n-ruby-5 bg-n-ruby-2 text-n-ruby-11 px-4 py-3 text-sm flex items-start gap-2"
          role="alert"
        >
          <span
            class="i-lucide-alert-circle size-4 mt-0.5 flex-shrink-0"
            aria-hidden="true"
          />
          <span>{{ serverError }}</span>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="col-span-2">
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ t('APPOINTMENTS.SERVICES.FORM.NAME') }}
              <span class="text-n-ruby-9" aria-hidden="true">&ast;</span>
            </label>
            <input
              v-model="form.name"
              type="text"
              :class="clientErrors.name ? 'border-n-ruby-7' : 'border-n-strong'"
              class="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-n-brand"
              @input="clientErrors.name = ''"
            />
            <p v-if="clientErrors.name" class="mt-1 text-xs text-n-ruby-11">
              {{ clientErrors.name }}
            </p>
          </div>
          <div class="col-span-2">
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ t('APPOINTMENTS.SERVICES.FORM.DESCRIPTION') }}
            </label>
            <textarea
              v-model="form.description"
              rows="4"
              class="w-full border border-n-strong rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-n-brand resize-none"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ t('APPOINTMENTS.SERVICES.FORM.DURATION_MINUTES') }}
              <span class="text-n-ruby-9" aria-hidden="true">&ast;</span>
            </label>
            <input
              v-model.number="form.duration_minutes"
              type="number"
              min="1"
              :class="
                clientErrors.duration_minutes
                  ? 'border-n-ruby-7'
                  : 'border-n-strong'
              "
              class="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-n-brand"
              @input="clientErrors.duration_minutes = ''"
            />
            <p
              v-if="clientErrors.duration_minutes"
              class="mt-1 text-xs text-n-ruby-11"
            >
              {{ clientErrors.duration_minutes }}
            </p>
          </div>
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ t('APPOINTMENTS.SERVICES.FORM.DEFAULT_PRICE') }}
            </label>
            <input
              :value="priceBRL"
              type="number"
              min="0"
              step="0.01"
              class="w-full border border-n-strong rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-n-brand"
              @input="onPriceInput"
            />
          </div>
        </div>

        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ t('APPOINTMENTS.SERVICES.FORM.COLOR') }}
          </label>
          <div class="flex items-center gap-3">
            <input
              v-model="form.color"
              type="color"
              class="h-9 w-16 border border-n-strong rounded cursor-pointer"
            />
            <span class="text-sm text-n-slate-11">{{ form.color }}</span>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <input
            id="online-available"
            v-model="form.online_available"
            type="checkbox"
            class="rounded border-n-strong"
          />
          <label
            for="online-available"
            class="text-sm text-n-slate-12 select-none cursor-pointer"
          >
            {{ t('APPOINTMENTS.SERVICES.FORM.ONLINE_AVAILABLE') }}
          </label>
        </div>

        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ t('APPOINTMENTS.SERVICES.FORM.REMINDER_TEMPLATES') }}
          </label>
          <ReminderTemplatesEditor v-model="form.reminder_templates" />
        </div>
      </div>

      <div
        class="flex items-center justify-end gap-3 px-6 py-4 border-t border-n-weak"
      >
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-1 rounded-lg"
          @click="$emit('close')"
        >
          {{ t('APPOINTMENTS.SERVICES.FORM.CANCEL') }}
        </button>
        <button
          type="button"
          :disabled="isSubmitting"
          class="px-4 py-2 text-sm font-medium text-white bg-n-brand hover:bg-n-brand/90 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          @click="handleSubmit"
        >
          {{ t('APPOINTMENTS.SERVICES.FORM.SAVE') }}
        </button>
      </div>
    </div>
  </div>
</template>
